import React, { useState } from 'react';
import { ref, set } from 'firebase/database';
import { db } from './firebase';
import s from './styles';

const REGIONS = [
  'Greater Accra', 'Ashanti', 'Western', 'Eastern', 'Central',
  'Northern', 'Upper East', 'Upper West', 'Volta', 'Brong-Ahafo',
  'Oti', 'Savannah', 'North East', 'Bono East', 'Ahafo', 'Western North',
];

export default function RegisterPage({ onSwitch }) {
  const [form, setForm] = useState({
    pharmacyName: '', ownerName: '', email: '',
    phone: '', password: '', location: '',
    region: '', licenseNumber: '', pcgLicenseNumber: '', pcgLicenseExpiry: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.pharmacyName || !form.email || !form.password || !form.phone || !form.licenseNumber) {
      setError('Please fill in all required fields'); return;
    }
    if (form.password.length < 6) {
      setError('Password must be at least 6 characters'); return;
    }
    setLoading(true); setError('');
    try {
      // ── USE SECONDARY APP INSTANCE so admin session is NOT disturbed ──────
      // We use the REST API to create the account without signing in
      const apiKey = 'AIzaSyAr53vGzhbpYucEQK91OwEbrG-B3dw_XVg';
      const res = await fetch(
        `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${apiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: form.email.trim().toLowerCase(),
            password: form.password,
            returnSecureToken: true,
          }),
        }
      );
      const data = await res.json();

      if (data.error) {
        const msg =
          data.error.message === 'EMAIL_EXISTS' ? 'An account with this email already exists. Please sign in instead.' :
          data.error.message === 'INVALID_EMAIL' ? 'Please enter a valid email address.' :
          data.error.message === 'WEAK_PASSWORD : Password should be at least 6 characters' ? 'Password must be at least 6 characters.' :
          'Registration failed. Please try again.';
        setError(msg);
        setLoading(false);
        return;
      }

      const uid = data.localId;

      // Save pharmacy data to Firebase database
      await set(ref(db, `pharmacies/${uid}`), {
        uid,
        pharmacyName: form.pharmacyName,
        ownerName: form.ownerName,
        email: form.email.trim().toLowerCase(),
        phone: form.phone,
        location: form.location,
        region: form.region,
        licenseNumber: form.licenseNumber,
        ...(form.pcgLicenseNumber.trim() ? { pcgLicenseNumber: form.pcgLicenseNumber.trim() } : {}),
        ...(form.pcgLicenseExpiry ? { pcgLicenseExpiry: form.pcgLicenseExpiry } : {}),
        status: 'pending',
        isOpen: true,
        createdAt: new Date().toISOString(),
      });

      setSuccess(true);
    } catch (e) {
      setError('Registration failed. Please check your internet connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={s.authPage}>
        <div style={{ ...s.authCard, textAlign: 'center' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 10 }}>Application Submitted!</h2>
          <p style={{ color: '#666', fontSize: 14, lineHeight: 1.7, marginBottom: 24 }}>
            Thank you for registering <strong>{form.pharmacyName}</strong> on PharmDev.
            Our team will verify your FDA license and approve your account within 24 hours.
          </p>
          <div style={s.infoBox}>
            <p style={s.infoText}>
              📧 Once approved, sign in using:<br />
              <strong>{form.email}</strong><br />
              with the password you just set.
            </p>
          </div>
          <div style={{ marginTop: 20 }}>
            <p style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>
              Questions? Contact us at <strong>pharmdev.org@gmail.com</strong> or call <strong>055 190 6880</strong> / <strong>059 984 8708</strong>
            </p>
            <button style={s.loginBtn} onClick={onSwitch}>
              ← Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={s.authPage}>
      <div style={{ ...s.authCard, maxWidth: 560 }}>
        <div style={{ fontSize: 40, marginBottom: 10 }}>💊</div>
        <h1 style={{ ...s.authTitle, fontSize: 22 }}>Register Your Pharmacy</h1>
        <p style={s.authSub}>Join PharmDev and start delivering medicines across Ghana</p>

        <form onSubmit={handleRegister} style={{ width: '100%', textAlign: 'left' }}>
          <div style={s.formGrid}>
            <div style={s.fieldGroup}>
              <label style={s.label}>Pharmacy Name *</label>
              <input
                style={s.input}
                placeholder="e.g. HealthPlus Pharmacy"
                value={form.pharmacyName}
                onChange={e => upd('pharmacyName', e.target.value)}
              />
            </div>
            <div style={s.fieldGroup}>
              <label style={s.label}>Owner / Manager Name</label>
              <input
                style={s.input}
                placeholder="Full name"
                value={form.ownerName}
                onChange={e => upd('ownerName', e.target.value)}
              />
            </div>
            <div style={s.fieldGroup}>
              <label style={s.label}>Email Address *</label>
              <input
                style={s.input}
                type="email"
                placeholder="pharmacy@email.com"
                value={form.email}
                onChange={e => upd('email', e.target.value)}
              />
            </div>
            <div style={s.fieldGroup}>
              <label style={s.label}>Phone Number *</label>
              <input
                style={s.input}
                placeholder="+233 24 000 0000"
                value={form.phone}
                onChange={e => upd('phone', e.target.value)}
              />
            </div>
            <div style={s.fieldGroup}>
              <label style={s.label}>FDA License Number *</label>
              <input
                style={s.input}
                placeholder="e.g. FDA/PHR/2024/001"
                value={form.licenseNumber}
                onChange={e => upd('licenseNumber', e.target.value)}
              />
            </div>
            <div style={s.fieldGroup}>
              <label style={s.label}>PCG License Number (optional — can add later)</label>
              <input
                style={s.input}
                placeholder="Pharmacy Council of Ghana registration"
                value={form.pcgLicenseNumber}
                onChange={e => upd('pcgLicenseNumber', e.target.value)}
              />
            </div>
            <div style={s.fieldGroup}>
              <label style={s.label}>PCG License Expiry (optional — can add later)</label>
              <input
                type="date"
                style={s.input}
                value={form.pcgLicenseExpiry}
                onChange={e => upd('pcgLicenseExpiry', e.target.value)}
              />
            </div>
            <div style={s.fieldGroup}>
              <label style={s.label}>Region</label>
              <select
                style={s.select}
                value={form.region}
                onChange={e => upd('region', e.target.value)}
              >
                <option value="">Select region</option>
                {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div style={{ ...s.fieldGroup, gridColumn: '1 / -1' }}>
              <label style={s.label}>Pharmacy Address</label>
              <input
                style={s.input}
                placeholder="e.g. 12 Ring Road, Adum, Kumasi"
                value={form.location}
                onChange={e => upd('location', e.target.value)}
              />
            </div>

            {/* PASSWORD WITH EYE TOGGLE */}
            <div style={{ ...s.fieldGroup, gridColumn: '1 / -1' }}>
              <label style={s.label}>Password *</label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  style={{ ...s.input, paddingRight: 48 }}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="At least 6 characters"
                  value={form.password}
                  onChange={e => upd('password', e.target.value)}
                />
                <button
                  type="button"
                  style={{
                    position: 'absolute', right: 12,
                    background: 'none', border: 'none',
                    cursor: 'pointer', fontSize: 18, padding: 4,
                    color: '#888', lineHeight: 1,
                  }}
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {form.password.length > 0 && form.password.length < 6 && (
                <p style={{ fontSize: 12, color: '#EF4444', margin: '4px 0 0' }}>
                  Password too short — needs at least 6 characters
                </p>
              )}
              {form.password.length >= 6 && (
                <p style={{ fontSize: 12, color: '#1D9E75', margin: '4px 0 0' }}>
                  ✅ Password strength good
                </p>
              )}
            </div>
          </div>

          {error && (
            <div style={{ backgroundColor: '#FEE2E2', borderRadius: 8, padding: 10, marginBottom: 12 }}>
              <p style={{ color: '#B91C1C', fontSize: 13, margin: 0 }}>⚠️ {error}</p>
            </div>
          )}

          <div style={{ ...s.infoBox, marginBottom: 16 }}>
            <p style={{ ...s.infoText, fontSize: 12 }}>
              ⏳ After registration your account will be reviewed within 24 hours.
              You will be able to sign in once the PharmDev admin approves your license.
            </p>
          </div>

          <button
            type="submit"
            style={{ ...s.loginBtn, opacity: loading ? 0.6 : 1 }}
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit Application →'}
          </button>
        </form>

        <div style={{ marginTop: 20, textAlign: 'center' }}>
          <button
            style={{ background: 'none', border: 'none', color: '#1D9E75', cursor: 'pointer', fontSize: 13, fontWeight: 500 }}
            onClick={onSwitch}
          >
            Already registered? Sign in →
          </button>
        </div>
      </div>
    </div>
  );
}